/**
 * Configuración OIDC (Authorization Code + PKCE) para Entra ID / Keycloak.
 * La renovación automática usa refresh token (`useSilentRefresh: false`).
 * No hay fallback a iframe: en Entra/Keycloak embebido suele fallar
 * (cookies de terceros / X-Frame-Options) y retrasa el redirect a login.
 */
import { AuthConfig } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';

/** URI de callback OIDC según `appBasePath` del ambiente (alineado con `baseHref` de Angular). */
export function buildOidcRedirectUri(): string {
  const basePath = (environment.appBasePath ?? '').replace(/\/$/, '');
  const callbackPath = `${basePath}/auth/callback`;
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${callbackPath}`;
  }
  return callbackPath;
}

/** URI del iframe de renovación silenciosa (angular-oauth2-oidc). */
export function buildSilentRefreshUri(): string {
  const basePath = (environment.appBasePath ?? '').replace(/\/$/, '');
  const refreshPath = `${basePath}/silent-refresh.html`;
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${refreshPath}`;
  }
  return refreshPath;
}

/**
 * Indica si la URL pertenece al IdP (discovery / token). Esas llamadas no deben
 * llevar Bearer de la API ni disparar el reintento 401 de sesión.
 */
export function isOidcProviderUrl(url: string): boolean {
  const issuer = environment.keycloak.issuer?.trim() ?? '';
  if (!issuer || !url) {
    return false;
  }
  try {
    return url.startsWith(new URL(issuer).origin);
  } catch {
    return (
      url.includes('/.well-known/openid-configuration') ||
      url.includes('/oauth2/v2.0/token') ||
      url.includes('/protocol/openid-connect/')
    );
  }
}

/** Configuración OIDC (Authorization Code + PKCE) contra Entra ID o Keycloak. */
export function buildAuthConfig(): AuthConfig {
  return {
    issuer: environment.keycloak.issuer,
    clientId: environment.keycloak.clientId,
    redirectUri: buildOidcRedirectUri(),
    responseType: 'code',
    scope: environment.keycloak.scope,
    showDebugInformation: !environment.production,
    requireHttps: environment.production,
    strictDiscoveryDocumentValidation: false,
    useSilentRefresh: false,
    silentRefreshRedirectUri: buildSilentRefreshUri(),
    silentRefreshTimeout: 8000,
    timeoutFactor: 0.75,
  };
}
